/**
 * Wallet Data Aggregator
 * Unified wallet data service with multi-provider fallback
 *
 * Provider Priority:
 * 1. DeBank (primary) - Best for DeFi positions and multi-chain data
 * 2. Moralis (fallback) - Good token/NFT data coverage
 * 3. Alchemy (last resort) - Basic token balances for Ethereum
 *
 * Features:
 * - Automatic fallback when primary provider fails
 * - Data merging from multiple sources
 * - Confidence scoring based on data agreement
 */

import debankApi, { isDeBankProEnabled } from './debankApi';
import moralisApi, { isMoralisEnabled } from './moralisApi';
import { callCoinGeckoAPI } from './api';
import type {
  Chain,
  UnifiedToken,
  DeFiPosition,
  NFTItem,
  AggregatedWalletData
} from '../types/wallet.types';

// =====================================================
// CACHE CONFIGURATION
// =====================================================

const CACHE_KEY_PREFIX = 'wallet_data_cache_';
const CACHE_DURATION_MS = 5 * 60 * 1000; // 5 minutes
const DEBANK_CREDITS_EXHAUSTED_KEY = 'debank_credits_exhausted';

interface CachedWalletData {
  data: AggregatedWalletData;
  timestamp: number;
}

/**
 * Check if DeBank credits are exhausted (cached flag)
 */
export function isDebankCreditsExhausted(): boolean {
  try {
    const flag = localStorage.getItem(DEBANK_CREDITS_EXHAUSTED_KEY);
    if (!flag) return false;
    const data = JSON.parse(flag);
    // Reset after 1 hour
    if (Date.now() - data.timestamp > 60 * 60 * 1000) {
      localStorage.removeItem(DEBANK_CREDITS_EXHAUSTED_KEY);
      return false;
    }
    return true;
  } catch {
    return false;
  }
}

/**
 * Mark DeBank credits as exhausted
 */
function markDebankCreditsExhausted(): void {
  try {
    localStorage.setItem(DEBANK_CREDITS_EXHAUSTED_KEY, JSON.stringify({
      exhausted: true,
      timestamp: Date.now()
    }));
  } catch {
    // Ignore storage errors
  }
}

/**
 * Get cached wallet data from localStorage
 */
function getCachedWalletData(address: string): AggregatedWalletData | null {
  try {
    const key = CACHE_KEY_PREFIX + address.toLowerCase();
    const cached = localStorage.getItem(key);
    if (!cached) return null;

    const data: CachedWalletData = JSON.parse(cached);
    const now = Date.now();

    // Check if cache is still valid
    if (now - data.timestamp > CACHE_DURATION_MS) {
      // Cache expired, but return it anyway if DeBank is exhausted
      if (isDebankCreditsExhausted()) {
        console.log('📦 Using expired cache due to DeBank credit exhaustion');
        return data.data;
      }
      localStorage.removeItem(key);
      return null;
    }

    console.log(`📦 Using cached wallet data (${Math.round((now - data.timestamp) / 1000)}s old)`);
    return data.data;
  } catch {
    return null;
  }
}

/**
 * Save wallet data to localStorage cache
 */
function setCachedWalletData(address: string, data: AggregatedWalletData): void {
  try {
    const key = CACHE_KEY_PREFIX + address.toLowerCase();
    const cacheEntry: CachedWalletData = {
      data,
      timestamp: Date.now()
    };
    localStorage.setItem(key, JSON.stringify(cacheEntry));
    console.log('📦 Wallet data cached successfully');
  } catch (error) {
    console.warn('Failed to cache wallet data:', error);
  }
}

/**
 * Clear wallet cache for an address
 */
export function clearWalletCache(address: string): void {
  try {
    const key = CACHE_KEY_PREFIX + address.toLowerCase();
    localStorage.removeItem(key);
  } catch {
    // Ignore errors
  }
}

/**
 * Clear all wallet caches
 */
export function clearAllWalletCaches(): void {
  try {
    const keys = Object.keys(localStorage).filter(k => k.startsWith(CACHE_KEY_PREFIX));
    keys.forEach(k => localStorage.removeItem(k));
    localStorage.removeItem(DEBANK_CREDITS_EXHAUSTED_KEY);
  } catch {
    // Ignore errors
  }
}

// Check if Alchemy is configured
const isAlchemyEnabled = (): boolean => {
  // In production, Alchemy is always available via serverless proxy
  const isDev = import.meta.env.DEV;
  return isDev ? !!import.meta.env.VITE_ALCHEMY_API_KEY : true;
};

// In development: Use direct Alchemy API calls
// In production: Use Vercel serverless proxy
const isDev = import.meta.env.DEV;
const USE_ALCHEMY_PROXY = !isDev;

/**
 * Call Alchemy API via Vercel serverless proxy (for production)
 */
async function callAlchemyProxy(network: string, method: string, params: unknown[] = []): Promise<any> {
  const response = await fetch('/api/alchemy/proxy', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ network, method, params }),
  });

  if (!response.ok) {
    throw new Error(`Alchemy proxy error: ${response.status}`);
  }

  return response.json();
}

// Alchemy chain configurations
const ALCHEMY_CHAINS: { chain: Chain; network: string; nativeSymbol: string; nativeName: string; geckoId: string; logo: string }[] = [
  { chain: 'ethereum', network: 'eth-mainnet', nativeSymbol: 'ETH', nativeName: 'Ethereum', geckoId: 'ethereum', logo: 'https://assets.coingecko.com/coins/images/279/small/ethereum.png' },
  { chain: 'polygon', network: 'polygon-mainnet', nativeSymbol: 'MATIC', nativeName: 'Polygon', geckoId: 'matic-network', logo: 'https://assets.coingecko.com/coins/images/4713/small/matic-token-icon.png' },
  { chain: 'arbitrum', network: 'arb-mainnet', nativeSymbol: 'ETH', nativeName: 'Ethereum (Arbitrum)', geckoId: 'ethereum', logo: 'https://assets.coingecko.com/coins/images/279/small/ethereum.png' },
  { chain: 'optimism', network: 'opt-mainnet', nativeSymbol: 'ETH', nativeName: 'Ethereum (Optimism)', geckoId: 'ethereum', logo: 'https://assets.coingecko.com/coins/images/279/small/ethereum.png' },
  { chain: 'base', network: 'base-mainnet', nativeSymbol: 'ETH', nativeName: 'Ethereum (Base)', geckoId: 'ethereum', logo: 'https://assets.coingecko.com/coins/images/279/small/ethereum.png' },
];

// Note: BSC (BNB Chain) is NOT supported by Alchemy - use Moralis or DeBank for BSC

/**
 * Make an Alchemy JSON-RPC call (dev: direct, prod: proxy)
 */
async function alchemyJsonRpcCall(network: string, method: string, params: unknown[]): Promise<any> {
  if (USE_ALCHEMY_PROXY) {
    return callAlchemyProxy(network, method, params);
  }

  // Direct call in development
  const apiKey = import.meta.env.VITE_ALCHEMY_API_KEY;
  const baseUrl = `https://${network}.g.alchemy.com/v2/${apiKey}`;
  const response = await fetch(baseUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ jsonrpc: '2.0', method, params, id: 1 }),
  });
  return response.json();
}

/**
 * Fetch tokens from a specific Alchemy chain
 */
async function fetchAlchemyChainTokens(
  address: string,
  chainConfig: typeof ALCHEMY_CHAINS[0],
  nativePrices: Map<string, number>
): Promise<UnifiedToken[]> {
  const tokens: UnifiedToken[] = [];

  try {
    // Get native balance
    const balanceData = await alchemyJsonRpcCall(
      chainConfig.network,
      'eth_getBalance',
      [address, 'latest']
    );

    if (balanceData.result) {
      const nativeBalance = parseInt(balanceData.result, 16) / 1e18;
      if (nativeBalance > 0.0001) {
        const nativePrice = nativePrices.get(chainConfig.geckoId) || 0;
        tokens.push({
          symbol: chainConfig.nativeSymbol,
          name: chainConfig.nativeName,
          address: '0x0000000000000000000000000000000000000000',
          chain: chainConfig.chain,
          balance: nativeBalance,
          balanceUSD: nativeBalance * nativePrice,
          price: nativePrice,
          logoUrl: chainConfig.logo,
          isVerified: true,
          source: ['alchemy']
        });
      }
    }

    // Get ERC20 token balances
    const tokenData = await alchemyJsonRpcCall(
      chainConfig.network,
      'alchemy_getTokenBalances',
      [address]
    );

    if (tokenData.result?.tokenBalances) {
      // Limit to first 10 tokens per chain for performance
      const tokenBalances = tokenData.result.tokenBalances
        .filter((t: any) => t.tokenBalance && t.tokenBalance !== '0x0' && t.tokenBalance !== '0x')
        .slice(0, 10);

      for (const token of tokenBalances) {
        try {
          // Get token metadata
          const metaData = await alchemyJsonRpcCall(
            chainConfig.network,
            'alchemy_getTokenMetadata',
            [token.contractAddress]
          );
          const metadata = metaData.result;

          if (!metadata || !metadata.symbol) continue;

          const balance = parseInt(token.tokenBalance, 16) / Math.pow(10, metadata.decimals || 18);
          if (balance <= 0) continue;

          tokens.push({
            symbol: metadata.symbol,
            name: metadata.name || metadata.symbol,
            address: token.contractAddress,
            chain: chainConfig.chain,
            balance,
            balanceUSD: 0, // Price not available from Alchemy
            price: 0,
            logoUrl: metadata.logo,
            isVerified: false,
            source: ['alchemy']
          });
        } catch {
          // Skip tokens with errors
        }
      }
    }
  } catch (error) {
    console.warn(`⚠️ Alchemy ${chainConfig.chain} fetch failed:`, error);
  }

  return tokens;
}

/**
 * Convert Alchemy token data to UnifiedToken format (multi-chain)
 */
async function getAlchemyTokensAsUnified(address: string): Promise<UnifiedToken[]> {
  // Fetch native token prices first (uses proxy in production)
  const nativePrices = new Map<string, number>();
  try {
    const geckoIds = [...new Set(ALCHEMY_CHAINS.map(c => c.geckoId))];
    const priceData = await callCoinGeckoAPI('/simple/price', {
      ids: geckoIds.join(','),
      vs_currencies: 'usd'
    });
    Object.entries(priceData).forEach(([id, data]: [string, any]) => {
      nativePrices.set(id, data.usd || 0);
    });
  } catch {
    // Use fallback prices
    nativePrices.set('ethereum', 2000);
    nativePrices.set('matic-network', 0.5);
  }

  // Fetch from all chains in parallel
  const results = await Promise.allSettled(
    ALCHEMY_CHAINS.map(chainConfig => fetchAlchemyChainTokens(address, chainConfig, nativePrices))
  );

  // Merge results
  const allTokens: UnifiedToken[] = [];
  results.forEach(result => {
    if (result.status === 'fulfilled') {
      allTokens.push(...result.value);
    }
  });

  console.log(`✅ Alchemy multi-chain: Found ${allTokens.length} tokens across ${ALCHEMY_CHAINS.length} chains`);

  return allTokens.sort((a, b) => b.balanceUSD - a.balanceUSD);
}

// Provider availability status
export interface ProviderStatus {
  debank: { available: boolean; pro: boolean };
  moralis: { available: boolean };
  alchemy: { available: boolean };
}

/**
 * Check which providers are available
 */
export function getProviderStatus(): ProviderStatus {
  return {
    debank: {
      available: isDeBankProEnabled(),
      pro: isDeBankProEnabled()
    },
    moralis: {
      available: isMoralisEnabled()
    },
    alchemy: {
      available: isAlchemyEnabled()
    }
  };
}

/**
 * Get best available provider for wallet data
 */
export function getBestProvider(): 'debank' | 'moralis' | 'alchemy' | null {
  const status = getProviderStatus();

  if (status.debank.available) return 'debank';
  if (status.moralis.available) return 'moralis';
  if (status.alchemy.available) return 'alchemy';
  return null;
}

/**
 * Merge tokens from multiple sources, avoiding duplicates
 */
function mergeTokens(
  debankTokens: UnifiedToken[],
  moralisTokens: UnifiedToken[]
): UnifiedToken[] {
  const tokenMap = new Map<string, UnifiedToken>();

  // Add DeBank tokens first (primary)
  debankTokens.forEach(token => {
    const key = `${token.chain}:${token.address.toLowerCase()}`;
    tokenMap.set(key, token);
  });

  // Merge Moralis tokens (fill gaps)
  moralisTokens.forEach(token => {
    const key = `${token.chain}:${token.address.toLowerCase()}`;
    const existing = tokenMap.get(key);

    if (!existing) {
      // New token not in DeBank
      tokenMap.set(key, token);
    } else {
      // Merge sources
      existing.source = [...new Set([...existing.source, ...token.source])];
      // Use Moralis price change if missing from DeBank
      if (!existing.priceChange24h && token.priceChange24h) {
        existing.priceChange24h = token.priceChange24h;
      }
    }
  });

  return Array.from(tokenMap.values())
    .sort((a, b) => b.balanceUSD - a.balanceUSD);
}

/**
 * Merge DeFi positions from multiple sources
 */
function mergeDeFiPositions(
  debankPositions: DeFiPosition[],
  moralisPositions: DeFiPosition[]
): DeFiPosition[] {
  const positionMap = new Map<string, DeFiPosition>();

  // Add DeBank positions first
  debankPositions.forEach(pos => {
    const key = `${pos.chain}:${pos.protocolId}:${pos.type}`;
    positionMap.set(key, pos);
  });

  // Merge Moralis positions (fill gaps)
  moralisPositions.forEach(pos => {
    const key = `${pos.chain}:${pos.protocolId}:${pos.type}`;
    if (!positionMap.has(key)) {
      positionMap.set(key, pos);
    }
  });

  return Array.from(positionMap.values())
    .sort((a, b) => b.netValueUSD - a.netValueUSD);
}

/**
 * Merge NFTs from multiple sources
 */
function mergeNFTs(
  debankNFTs: NFTItem[],
  moralisNFTs: NFTItem[]
): NFTItem[] {
  const nftMap = new Map<string, NFTItem>();

  // Add DeBank NFTs first
  debankNFTs.forEach(nft => {
    const key = `${nft.chain}:${nft.contractAddress}:${nft.tokenId}`;
    nftMap.set(key, nft);
  });

  // Merge Moralis NFTs (fill gaps, often has better images)
  moralisNFTs.forEach(nft => {
    const key = `${nft.chain}:${nft.contractAddress}:${nft.tokenId}`;
    const existing = nftMap.get(key);

    if (!existing) {
      nftMap.set(key, nft);
    } else if (!existing.imageUrl && nft.imageUrl) {
      // Use Moralis image if DeBank doesn't have one
      existing.imageUrl = nft.imageUrl;
    }
  });

  return Array.from(nftMap.values());
}

/**
 * Calculate confidence score based on data sources
 */
function calculateConfidence(sources: string[], tokenCount: number): number {
  let confidence = 50; // Base confidence

  if (sources.includes('debank')) {
    confidence += 30; // DeBank is high quality
  }
  if (sources.includes('moralis')) {
    confidence += 15;
  }
  if (sources.includes('alchemy')) {
    confidence += 10; // Alchemy is reliable but limited
  }
  if (sources.length > 1) {
    confidence += 5; // Multi-source bonus
  }
  if (tokenCount > 0) {
    confidence = Math.min(confidence, 95); // Cap at 95
  }

  return confidence;
}

/**
 * Get complete wallet data with automatic fallback
 * Primary: DeBank → Fallback: Moralis → Cache
 */
export async function getAggregatedWalletData(
  address: string,
  options: {
    includeNFTs?: boolean;
    includeDeFi?: boolean;
    chains?: Chain[];
    skipCache?: boolean;
  } = {}
): Promise<AggregatedWalletData> {
  const { includeNFTs = true, includeDeFi = true, skipCache = false } = options;
  const status = getProviderStatus();

  // Check cache first (if not skipped)
  if (!skipCache) {
    const cachedData = getCachedWalletData(address);
    if (cachedData) {
      return cachedData;
    }
  }

  let tokens: UnifiedToken[] = [];
  let defiPositions: DeFiPosition[] = [];
  let nfts: NFTItem[] = [];
  let totalValueUSD = 0;
  const sources: string[] = [];
  let debankCreditsExhausted = false;

  // Try DeBank first (primary) - skip if credits are known to be exhausted
  if (status.debank.available && !isDebankCreditsExhausted()) {
    try {
      const debankData = await debankApi.getCompleteWalletData(address);
      tokens = debankData.tokens;
      defiPositions = includeDeFi ? debankData.defiPositions : [];
      nfts = includeNFTs ? debankData.nfts : [];
      totalValueUSD = debankData.totalValueUSD;
      sources.push('debank');

      console.log(`✅ DeBank: Found ${tokens.length} tokens, ${defiPositions.length} DeFi positions`);
    } catch (error: any) {
      // Check if this is a credit exhaustion error (403)
      const errorMessage = error?.message || error?.response?.data?.message || '';
      const isCreditsExhausted =
        error?.response?.status === 403 ||
        errorMessage.toLowerCase().includes('insufficient units') ||
        errorMessage.toLowerCase().includes('limited');

      if (isCreditsExhausted) {
        console.warn('⚠️ DeBank credits exhausted, marking for 1 hour');
        markDebankCreditsExhausted();
        debankCreditsExhausted = true;
      }

      console.warn('⚠️ DeBank failed, trying Moralis fallback:', error);
    }
  } else if (isDebankCreditsExhausted()) {
    console.log('⚠️ DeBank skipped - credits exhausted');
    debankCreditsExhausted = true;
  }

  // Use Moralis as fallback or supplement
  if (status.moralis.available) {
    try {
      console.log('📡 Fetching from Moralis...');
      const [moralisTokens, moralisNFTs, moralisDeFi] = await Promise.all([
        moralisApi.getAllTokenBalances(address),
        includeNFTs ? moralisApi.getAllNFTs(address) : Promise.resolve([]),
        includeDeFi ? moralisApi.getAllDeFiPositions(address) : Promise.resolve([])
      ]);

      if (moralisTokens.length > 0) {
        if (tokens.length === 0) {
          // Moralis is primary (DeBank failed or unavailable)
          tokens = moralisTokens;
          nfts = moralisNFTs;
          defiPositions = moralisDeFi;
          totalValueUSD = tokens.reduce((sum, t) => sum + t.balanceUSD, 0);
        } else {
          // Merge data from both sources
          tokens = mergeTokens(tokens, moralisTokens);
          nfts = mergeNFTs(nfts, moralisNFTs);
          defiPositions = mergeDeFiPositions(defiPositions, moralisDeFi);
        }
        sources.push('moralis');
      }

      console.log(`✅ Moralis: Found ${moralisTokens.length} tokens, ${moralisDeFi.length} DeFi positions`);
    } catch (error) {
      console.warn('⚠️ Moralis fallback failed:', error);
    }
  }

  // Fallback to Alchemy if no tokens found yet (DeBank and Moralis failed or returned empty)
  if (tokens.length === 0 && status.alchemy.available) {
    try {
      console.log('📡 Using Alchemy fallback...');
      const alchemyTokens = await getAlchemyTokensAsUnified(address);
      if (alchemyTokens.length > 0) {
        tokens = alchemyTokens;
        totalValueUSD = tokens.reduce((sum, t) => sum + t.balanceUSD, 0);
        sources.push('alchemy');
      }
      console.log(`✅ Alchemy: Found ${alchemyTokens.length} tokens`);
    } catch (error) {
      console.warn('⚠️ Alchemy fallback failed:', error);
    }
  }

  // Even if we have some tokens, supplement with Alchemy for additional chains if needed
  if (tokens.length > 0 && tokens.length < 3 && status.alchemy.available && !sources.includes('alchemy')) {
    try {
      console.log('📡 Supplementing with Alchemy data...');
      const alchemyTokens = await getAlchemyTokensAsUnified(address);
      if (alchemyTokens.length > 0) {
        // Add tokens not already in the list
        const existingKeys = new Set(tokens.map(t => `${t.chain}:${t.address.toLowerCase()}`));
        const newTokens = alchemyTokens.filter(t => !existingKeys.has(`${t.chain}:${t.address.toLowerCase()}`));
        if (newTokens.length > 0) {
          tokens = [...tokens, ...newTokens].sort((a, b) => b.balanceUSD - a.balanceUSD);
          totalValueUSD = tokens.reduce((sum, t) => sum + t.balanceUSD, 0);
          sources.push('alchemy');
          console.log(`✅ Alchemy supplement: Added ${newTokens.length} additional tokens`);
        }
      }
    } catch (error) {
      console.warn('⚠️ Alchemy supplement failed:', error);
    }
  }

  // If no providers available, try to return cached data
  if (sources.length === 0) {
    console.error('❌ No wallet data providers available, checking cache...');

    // Try to return cached data (even if expired)
    const cachedData = getCachedWalletData(address);
    if (cachedData) {
      console.log('📦 Returning cached data as fallback');
      return {
        ...cachedData,
        lastUpdated: cachedData.lastUpdated + ' (cached)'
      };
    }

    return {
      address: address.toLowerCase(),
      totalValueUSD: 0,
      tokens: [],
      defiPositions: [],
      nfts: [],
      chainsUsed: [],
      lastUpdated: new Date().toISOString(),
      sources: [],
      confidence: 0
    };
  }

  // Determine chains used
  const chainsUsed = [...new Set([
    ...tokens.map(t => t.chain),
    ...defiPositions.map(p => p.chain),
    ...nfts.map(n => n.chain)
  ])] as Chain[];

  // Calculate total value if not from DeBank
  if (totalValueUSD === 0) {
    totalValueUSD = tokens.reduce((sum, t) => sum + t.balanceUSD, 0) +
                    defiPositions.reduce((sum, p) => sum + p.netValueUSD, 0);
  }

  const result: AggregatedWalletData = {
    address: address.toLowerCase(),
    totalValueUSD,
    tokens,
    defiPositions,
    nfts,
    chainsUsed,
    lastUpdated: new Date().toISOString(),
    sources: sources as AggregatedWalletData['sources'],
    confidence: calculateConfidence(sources, tokens.length)
  };

  // Save to cache for future use
  setCachedWalletData(address, result);

  return result;
}

/**
 * Get token balances only (faster than full data)
 */
export async function getTokenBalances(
  address: string,
  chain?: Chain
): Promise<UnifiedToken[]> {
  const status = getProviderStatus();

  // Try DeBank first
  if (status.debank.available) {
    try {
      const tokens = chain
        ? await debankApi.getTokenBalancesByChain(address, chain)
        : await debankApi.getAllTokenBalances(address);

      if (tokens.length > 0) {
        return tokens;
      }
    } catch (error) {
      console.warn('⚠️ DeBank token fetch failed:', error);
    }
  }

  // Fallback to Moralis
  if (status.moralis.available) {
    try {
      const tokens = chain
        ? await moralisApi.getTokenBalances(address, chain)
        : await moralisApi.getAllTokenBalances(address);

      if (tokens.length > 0) {
        return tokens;
      }
    } catch (error) {
      console.warn('⚠️ Moralis token fetch failed:', error);
    }
  }

  // Fallback to Alchemy (Ethereum only)
  if (status.alchemy.available && (!chain || chain === 'ethereum')) {
    try {
      return await getAlchemyTokensAsUnified(address);
    } catch (error) {
      console.warn('⚠️ Alchemy token fetch failed:', error);
    }
  }

  return [];
}

/**
 * Get DeFi positions only
 */
export async function getDeFiPositions(
  address: string
): Promise<DeFiPosition[]> {
  const status = getProviderStatus();

  // Try DeBank first (best DeFi data)
  if (status.debank.available) {
    try {
      const positions = await debankApi.getAllDeFiPositions(address);
      if (positions.length > 0) {
        return positions;
      }
    } catch (error) {
      console.warn('⚠️ DeBank DeFi fetch failed:', error);
    }
  }

  // Fallback to Moralis
  if (status.moralis.available) {
    try {
      return await moralisApi.getAllDeFiPositions(address);
    } catch (error) {
      console.warn('⚠️ Moralis DeFi fetch failed:', error);
    }
  }

  return [];
}

/**
 * Get NFTs only
 */
export async function getNFTs(
  address: string,
  chain?: Chain
): Promise<NFTItem[]> {
  const status = getProviderStatus();

  // Try DeBank first
  if (status.debank.available) {
    try {
      const nfts = await debankApi.getNFTList(address);
      if (chain) {
        return nfts.filter(n => n.chain === chain);
      }
      return nfts;
    } catch (error) {
      console.warn('⚠️ DeBank NFT fetch failed:', error);
    }
  }

  // Fallback to Moralis
  if (status.moralis.available) {
    try {
      const nfts = chain
        ? await moralisApi.getNFTs(address, chain)
        : await moralisApi.getAllNFTs(address);

      return nfts;
    } catch (error) {
      console.warn('⚠️ Moralis NFT fetch failed:', error);
    }
  }

  return [];
}

/**
 * Get total portfolio value
 */
export async function getTotalValue(address: string): Promise<number> {
  const status = getProviderStatus();

  // DeBank has a dedicated endpoint for this
  if (status.debank.available) {
    try {
      return await debankApi.getTotalBalance(address);
    } catch (error) {
      console.warn('⚠️ DeBank total balance failed:', error);
    }
  }

  // Calculate from tokens for Moralis
  if (status.moralis.available) {
    try {
      const tokens = await moralisApi.getAllTokenBalances(address);
      return tokens.reduce((sum, t) => sum + t.balanceUSD, 0);
    } catch (error) {
      console.warn('⚠️ Moralis total calculation failed:', error);
    }
  }

  // Calculate from Alchemy
  if (status.alchemy.available) {
    try {
      const tokens = await getAlchemyTokensAsUnified(address);
      return tokens.reduce((sum, t) => sum + t.balanceUSD, 0);
    } catch (error) {
      console.warn('⚠️ Alchemy total calculation failed:', error);
    }
  }

  return 0;
}

// Export unified API
export const walletDataAggregator = {
  getProviderStatus,
  getBestProvider,
  getAggregatedWalletData,
  getTokenBalances,
  getDeFiPositions,
  getNFTs,
  getTotalValue,
  // Cache functions
  isDebankCreditsExhausted,
  clearWalletCache,
  clearAllWalletCaches
};

export default walletDataAggregator;
